import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCheck, faChevronDown, faChevronUp } from '@/fontawesome';
import { BOOST_PACKAGE, BOOST_URL } from '@/consts/pricing';

/**
 * Karte für den Trading Boost auf /pricing. Optisch identisch zur PricingCard, aber ohne
 * Preis und ohne Checkout — der Einstieg läuft ausschließlich über svenbolz.com.
 *
 * Die 5-Zeilen-Subgrid-Struktur (Header / Preis / CTA / Features / Ideal für) muss erhalten
 * bleiben, damit die Karte neben der Daytrading-PricingCard zeilenweise ausgerichtet ist.
 * Zeile 2 bleibt als leerer Platzhalter stehen, weil es hier keinen Preis gibt.
 */
export default function BoostCard() {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = () => {
    setIsExpanded(prev => !prev);
  };

  return (
    <div className="relative bg-white dark:bg-gray-800 rounded-xl p-6 border transition-all flex flex-col md:grid md:grid-rows-subgrid md:row-span-5 h-full border-gray-200 dark:border-gray-700 shadow-md hover:shadow-xl hover:-translate-y-1">
      {/* Row 1: Package Header */}
      <div className="mb-6 md:mb-0">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          {BOOST_PACKAGE.name}
        </h3>
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {BOOST_PACKAGE.subtitle}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
          {BOOST_PACKAGE.shortDescription}
        </p>
      </div>

      {/* Row 2: Preis-Platzhalter — hält die Subgrid-Zeile für die Ausrichtung frei */}
      <div />

      {/* Row 3: CTA (extern) + Mobile Toggle */}
      <div>
        <a
          href={BOOST_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full py-3 px-4 rounded-lg font-semibold text-white shadow-lg transition-all transform bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 hover:shadow-xl hover:-translate-y-0.5 cursor-pointer inline-flex items-center justify-center gap-2"
        >
          Mehr zum Trading Boost
          <FontAwesomeIcon icon={faArrowRight} className="w-4 h-4" />
        </a>
        <button
          onClick={handleToggle}
          className="md:hidden w-full mt-4 mb-6 py-2 px-4 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors cursor-pointer flex items-center justify-center gap-2"
        >
          <span>{isExpanded ? 'Details ausblenden' : 'Details anzeigen'}</span>
          <FontAwesomeIcon
            icon={isExpanded ? faChevronUp : faChevronDown}
            className="w-3 h-3"
          />
        </button>
      </div>

      {/* Row 4: Features */}
      <div className={`${isExpanded ? 'block' : 'hidden'} md:block`}>
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mb-4" />
        <div>
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
            Du bekommst
          </h4>
          <ul className="space-y-2">
            {BOOST_PACKAGE.features.map((feature, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                <FontAwesomeIcon
                  icon={faCheck}
                  className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 shrink-0"
                />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Row 5: Ideal für */}
      <div className={`${isExpanded ? 'block' : 'hidden'} md:block`}>
        <div className="pt-4 pb-4 border-t border-gray-200 dark:border-gray-700">
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
            Ideal für
          </h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {BOOST_PACKAGE.idealFor}
          </p>
        </div>
      </div>
    </div>
  );
}
